import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { Button } from '@/components/button/Button'
import { useAppDispatch } from '@/store/store-hooks'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { getTableChats } from '../fetch/get-table-chats'
import { postCreateTableChat } from '../fetch/post-create-table-chat'
import type { TableChat } from '../chats'
import { ChatHistory } from '@/assets/icons'
import { TruncatedText } from '@/components/truncated-text/TruncatedText'

export default function TableChatPicker({
  tableId,
  matterId,
  onOpenChat,
  historyOnly = false,
  currentConversationId,
}: {
  tableId: string
  matterId: string
  onOpenChat: (id: string) => void
  historyOnly?: boolean
  currentConversationId?: string | null
}) {
  const dispatch = useAppDispatch()
  const [chats, setChats] = useState<TableChat[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    const controller = new AbortController()
    setError(null)
    getTableChats(tableId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setChats(result)
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Unable to load chats.')
      })
    return () => controller.abort()
  }, [tableId, refresh])
  return (
    <div className={historyOnly ? 'flex min-w-0 flex-col gap-2' : 'flex flex-col gap-4 p-4'}>
      {!historyOnly && (
        <Button
          type="button"
          fullWidth
          color="primary"
          variant="filled"
          loading={creating}
          aria-busy={creating}
          onClick={async () => {
            if (creating) return
            setCreating(true)
            setError(null)
            try {
              const chat = await postCreateTableChat(tableId)
              dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: true }))
              onOpenChat(chat.id)
            } catch (reason) {
              setError(reason instanceof Error ? reason.message : 'Unable to create chat.')
            } finally {
              setCreating(false)
            }
          }}
        >
          New chat
        </Button>
      )}
      {error && (
        <div role="alert" className={clsx('text-sm text-red-800', historyOnly && 'px-2')}>
          {error}{' '}
          <Button variant="plain" size="sm" onClick={() => setRefresh((value) => value + 1)}>
            Retry
          </Button>
        </div>
      )}
      {!chats && !error && (
        <p role="status" className={clsx('text-sm text-brand-neutral-600', historyOnly && 'px-2')}>
          Loading chats…
        </p>
      )}
      <div className={historyOnly ? 'max-h-80 overflow-y-auto' : 'max-h-80 overflow-auto divide-y divide-brand-neutral-200'}>
        {chats?.map((chat) => (
          <button
            key={chat.id}
            type="button"
            disabled={creating || chat.id === currentConversationId}
            aria-current={chat.id === currentConversationId ? 'page' : undefined}
            onClick={() => onOpenChat(chat.id)}
            className={clsx(
              historyOnly
                ? 'flex w-full items-center gap-2 rounded p-2 text-left text-sm text-brand-neutral-700 enabled:hover:bg-brand-neutral-100 focus-visible:outline-brand-500'
                : 'flex w-full flex-col gap-1 rounded p-3 text-left hover:bg-brand-neutral-100 disabled:opacity-50',
              chat.id === currentConversationId && 'font-semibold'
            )}
          >
            {historyOnly ? (
              <>
                <ChatHistory className="size-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  <TruncatedText>{chat.title}</TruncatedText>
                </span>
              </>
            ) : (
              <span className="text-sm font-medium">{chat.title}</span>
            )}
            {!historyOnly && (
              <span className="text-xs text-brand-neutral-600">
                {new Date(chat.updated_at).toLocaleDateString()}
                {!chat.can_continue && ' · Read only'}
              </span>
            )}
          </button>
        ))}
      </div>
      {chats?.length === 0 && (
        <p className={clsx('text-sm text-brand-neutral-600', historyOnly && 'px-2')}>No chats linked yet. Start a new chat to discuss this table.</p>
      )}
    </div>
  )
}
